import React, { useEffect } from 'react'
import { createScript, setEntityError } from '../../../redux/slices'
import { useDispatch, useSelector } from 'react-redux'
import { showToast } from '../../../utils/types'
import './ScriptUpload.css'
import 'devextreme/dist/css/dx.common.css'
import 'devextreme/dist/css/dx.light.css'
import JSZip from 'jszip'
import ICSTextInput from '../../../components/ICSTextInput/ICSTextInput'
import ICSTextAreaInput from '../../../components/ICSTextAreaInput/ICSTextAreaInput'
import ICSFileInput from '../../../components/ICSFileInput/ICSFileInput'
import { Col, Row } from 'reactstrap'
import Joi from 'joi'
import { joiResolver } from '@hookform/resolvers/joi'
import { useForm } from 'react-hook-form'
import { Form, FormGroup } from 'reactstrap'
import ComponentBoxFooter from '../../../components/ComponentBox/ComponentBoxFooter/ComponentBoxFooter'
import ICSButton from '../../../components/ICS/Button/ICSButton'
import { AppDispatch } from '../../../services/lib'
import { DefaultState } from '../../../types'
import { hasInvalidField, invalidFieldsMessage } from '../../../utils/helpers'
import ICSErrorCard from '../../../components/ICSErrorCard/ICSErrorCard'
import { RequestErrorResponse } from '../../../services/lib/types'

const defaultJoiSchema = {
  file: Joi.string().required().messages({
    'string.empty': `Required field`,
  }),
  filename: Joi.string().required().messages({
    'string.empty': `Required field`,
  }),
  fileUpload: Joi.string().optional().allow(''),
  name: Joi.string().required().min(3).max(30).messages({
    'string.empty': `Required field`,
    'string.min': `Must be at lease {#limit} in length`,
  }),
  description: Joi.string().min(0).max(255).allow(''),
}

const schema = Joi.object().keys(defaultJoiSchema)

const ScriptUpload = ({ hideModal }) => {
  const dispatch: AppDispatch = useDispatch()
  const scriptErrors = useSelector((state: DefaultState) => state.scripts.error)
  const shouldShowErrorCard =
    scriptErrors && (!scriptErrors.fields || scriptErrors.fields.length === 0)

  const {
    formState: { errors, isSubmitted, isValid },
    handleSubmit,
    control,
    setValue,
    trigger,
    setError,
  } = useForm({
    mode: 'onChange',
    resolver: joiResolver(schema),
  })

  useEffect(() => {
    if (scriptErrors) {
      scriptErrors.fields.forEach(async errorObj => {
        setError(errorObj.field, {
          type: 'manual',
          message: errorObj.message,
        })
      })
    }
  }, [scriptErrors])

  const fileChosen = async (data: File) => {
    if (!data) return ''
    const zip = new JSZip()
    const fileObj = await zip.loadAsync(data)

    const fileNames = Object.keys(fileObj.files)

    let pathMain = false
    if (!fileNames.includes('__main__.py')) {
      fileNames.forEach(name => {
        if (name.includes('__main__.py')) {
          pathMain = true
        }
      })
    }

    if (fileNames.includes('__main__.py') || pathMain) {
      const formattedFileString = new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(data)
        reader.onload = () => {
          resolve(reader.result)
        }
        reader.onerror = error => reject(error)
      }).then(data => {
        const fileString = data as string
        return fileString.substring(
          fileString.lastIndexOf(',') + 1,
          fileString.length
        )
      })

      return formattedFileString
    } else {
      showToast('File must contain a __main__.py', 'Error', 2500)
    }
  }

  const buttonClick = () => document.getElementById('fileFileInput')?.click()

  const onSubmit = async formValues => {
    const { file, description, name } = formValues
    const createdScript = await dispatch(
      createScript({
        file,
        description,
        name,
      })
    )

    if (createdScript.type.split('/').pop() === 'fulfilled') {
      hideModal()
    } else {
      const verifyFields = await hasInvalidField(
        Object.keys(defaultJoiSchema),
        (createdScript.payload as RequestErrorResponse).fields.map(
          (fieldObj: { field: string; message: string }) => fieldObj.field
        )
      )

      if (verifyFields.isInvalid) {
        dispatch(
          setEntityError({
            sliceName: 'scripts',
            entityName: 'Script',
            error: {
              code: 'error',
              message: invalidFieldsMessage,
              fields: verifyFields.fields,
            },
          })
        )
      }
    }
  }

  return (
    <>
      <Row>
        <Col xs={{ size: 12 }} className='wivi-col'>
          {shouldShowErrorCard ? (
            <ICSErrorCard
              code={scriptErrors.code}
              message={scriptErrors.message}
              fields={scriptErrors.fields}
            />
          ) : (
            <Form onSubmit={handleSubmit(onSubmit)}>
              <FormGroup>
                <Row>
                  <Col>
                    <ICSFileInput
                      name='fileUpload'
                      label='Upload Script'
                      validateFile={fileChosen}
                      buttonClick={buttonClick}
                      accept='.zip'
                      control={control}
                      errors={errors}
                      isSubmitted={isSubmitted}
                      setValue={setValue}
                      trigger={trigger}
                    />
                  </Col>
                  <ICSTextInput
                    name='filename'
                    hidden={true}
                    control={control}
                    errors={errors}
                    isSubmitted={isSubmitted}
                  />
                  <ICSTextInput
                    name='file'
                    hidden={true}
                    control={control}
                    errors={errors}
                    isSubmitted={isSubmitted}
                  />
                </Row>
                <Row>
                  <Col>
                    <ICSTextInput
                      label='Name'
                      name='name'
                      placeholder='Script Name...'
                      maxCharCount={30}
                      control={control}
                      errors={errors}
                      isSubmitted={isSubmitted}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <ICSTextAreaInput
                      label='Description'
                      name='description'
                      placeholder='Script Description...'
                      control={control}
                      errors={errors}
                      isSubmitted={isSubmitted}
                    />
                  </Col>
                </Row>
                <ComponentBoxFooter>
                  <ICSButton
                    id='formSubmit'
                    width={60}
                    useSubmitBehavior={true}
                    type='success'
                    disabled={!isValid}
                  >
                    Save
                  </ICSButton>
                </ComponentBoxFooter>
              </FormGroup>
            </Form>
          )}
        </Col>
      </Row>
    </>
  )
}

export default ScriptUpload
